// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Dialog.tsx
 * @input Uses React, DialogHTMLAttributes, ReactNode, container (Layout), DialogContext
 * @output Exports Dialog component, DialogProps, DialogVariant, DialogPurpose types
 * @position Core implementation; consumed by index.ts, tested by Dialog.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Dialog/Dialog.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Dialog/Dialog.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Dialog/index.ts (exports if types change)
 * - /apps/storybook/stories/Dialog.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Dialog/ (showcase blocks)
 */
import { useEffect, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useScrollLock } from "../hooks/useScrollLock.js";
import { hasActiveFocusTrapEscape, isImeKeyEvent } from "../hooks/useFocusTrap.js";
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, durationVars, easeVars, shadowVars } from "../theme/tokens.stylex.js";
import { container } from "../Layout/container.stylex.js";
import { paddingStyles, containerPaddingInlineVarStyles, containerPaddingBlockStartVarStyles, containerPaddingBlockEndVarStyles, spacingStepToToken } from "../Layout/padding.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { DialogContext } from "./DialogContext.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * Calculate a directional translate offset for dialog entry animation.
 * Returns a normalized vector from the trigger element toward the viewport
 * center, scaled to the given distance.
 */
import { jsx as _jsx } from "react/jsx-runtime";
function getDialogDirection(triggerEl, distance = 16) {
  const rect = triggerEl.getBoundingClientRect();
  const dx = rect.left + rect.width / 2 - window.innerWidth / 2;
  const dy = rect.top + rect.height / 2 - window.innerHeight / 2;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  return {
    x: Math.round(dx / dist * distance),
    y: Math.round(dy / dist * distance)
  };
}

/**
 * Extensible variant map for Dialog.
 *
 * Theme packages can add custom variants via TypeScript module augmentation:
 * @example
 * ```
 * declare module '@astryxdesign/core/Dialog' {
 *   interface DialogVariantMap {
 *     'drawer': true;
 *   }
 * }
 * ```
 */

/**
 * Dialog variant type
 * - standard: Normal dialog with configurable width/height
 * - fullscreen: Takes up the entire viewport
 *
 * Extensible via module augmentation of DialogVariantMap.
 */

/**
 * Dialog purpose type - controls dismissal behavior
 * - required: Mandatory flows (security, permissions) - disables all exit methods
 * - form: User forms/flows - prevents backdrop click, allows escape
 * - info: Informational flows - allows all exit methods
 */

/**
 * Position configuration for static dialog positioning
 */

const enterDirectional = "x11lauxq-B";

/**
 * Dialog styles using native <dialog> element
 * Uses ::backdrop pseudo-element for overlay
 */
const styles = {
  dialog: {
    kVAEAm: "xixxii4",
    kogj98: "x1bpp3o7",
    kmVPX3: "x1717udv",
    kWkggS: "x10xzikg",
    "--_dialog-radius": "xvuvksw",
    kaIpWk: "xuacgfc",
    kGVxlE: "x1kcpxr7",
    k1xSpc: "x1s85apg",
    kXwgrk: "xdt5ytf",
    kZKoxP: "xg7h5cd",
    kZeWKH: "xish69e",
    kSiTet: "xg01cxk",
    k44tkh: "xqgcaz",
    kyAemX: "x128ha8g",
    kWV6AL: "xskzprw",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  open: {
    k1xSpc: "x78zum5",
    kSiTet: "x1hc1fzr",
    kKVMdj: "x1ewfqum",
    $$css: true
  },
  backdrop: {
    kGyWv1: "xnixb3f",
    kba3nw: "x1abwkk1",
    $$css: true
  },
  fullscreen: {
    kzqmXN: "x1o6l61p",
    kZKoxP: "xtdtrs8",
    ks0D6T: "xlbgzzq",
    kskxy: "x1wj9ous",
    kaIpWk: "x2u8bby",
    kogj98: "x1ghz6dp",
    kpwlN0: "x10a8y8t",
    $$css: true
  },
  inner: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kUk6DE: "x12lumcd",
    kAzted: "x2lwn1j",
    kVQacm: "xb3r6kr",
    kaIpWk: "x1pjcqnp",
    $$css: true
  },
  inlineWrapper: {
    kmVPX3: "x1717udv",
    kWkggS: "x10xzikg",
    "--_dialog-radius": "xvuvksw",
    kaIpWk: "xuacgfc",
    kGVxlE: "x1kcpxr7",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kZKoxP: "xg7h5cd",
    kZeWKH: "xish69e",
    $$css: true
  }
};

// Dynamic styles for width, maxHeight, and position
const _temp = {
  ks0D6T: "x95b5qq",
  "$$css": true
};
const _temp2 = {
  kogj98: "x1ghz6dp",
  "$$css": true
};
const dynamicStyles = {
  sizing: (width, maxHeight) => [_temp, {
    kzqmXN: (typeof width === 'number' ? `${width}px` : width) != null ? "x5lhr3w" : typeof width === 'number' ? `${width}px` : width,
    kskxy: (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) != null ? "x1jols5v" : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof width === 'number' ? `${width}px` : width),
    "--x-maxHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight)
  }],
  position: (top, right, bottom, left) => [_temp2, {
    k87sOh: (top !== undefined ? formatPosition(top) : 'auto') != null ? "xjbys53" : top !== undefined ? formatPosition(top) : 'auto',
    kCIrl2: (right !== undefined ? formatPosition(right) : 'auto') != null ? "x7ok3n0" : right !== undefined ? formatPosition(right) : 'auto',
    krVfgx: (bottom !== undefined ? formatPosition(bottom) : 'auto') != null ? "x1nqzi6q" : bottom !== undefined ? formatPosition(bottom) : 'auto',
    kbCHJM: (left !== undefined ? formatPosition(left) : 'auto') != null ? "xgq9j65" : left !== undefined ? formatPosition(left) : 'auto',
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top !== undefined ? formatPosition(top) : 'auto'),
    "--x-right": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(right !== undefined ? formatPosition(right) : 'auto'),
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(bottom !== undefined ? formatPosition(bottom) : 'auto'),
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(left !== undefined ? formatPosition(left) : 'auto')
  }]
};

/**
 * Format position value - numbers become pixels, strings pass through, undefined becomes null
 */
function formatPosition(value) {
  if (value === undefined) {
    return null;
  }
  return typeof value === 'number' ? `${value}px` : value;
}
/**
 * A dialog component using the native <dialog> element.
 *
 * Designed to be used with Layout as its child for structured content.
 * Uses the browser's built-in modal behavior for optimal accessibility.
 *
 * @example
 * ```
 * const [isOpen, setIsOpen] = useState(false);
 * <Dialog isOpen={isOpen} onOpenChange={open => setIsOpen(open)}>
 *   <Layout
 *     header={<DialogHeader title="Title" onOpenChange={open => setIsOpen(open)} />}
 *     content={<LayoutContent>Content</LayoutContent>}
 *     footer={<LayoutFooter hasDivider>Actions</LayoutFooter>}
 *   />
 * </Dialog>
 * ```
 */
export function Dialog({
  isOpen,
  isInline = false,
  onOpenChange,
  width = 400,
  maxHeight = '75vh',
  position,
  variant = 'standard',
  purpose = 'info',
  padding,
  children,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  // When no explicit padding prop, use theme default (--astryx-dialog-padding)
  const useThemeDefault = padding == null;
  const effectivePadding = padding ?? 4;
  const paddingToken = spacingStepToToken[effectivePadding];
  const isFullscreen = variant === 'fullscreen';
  const dialogContextValue = useMemo(() => ({
    isInline
  }), [isInline]);
  const dialogRef = useRef(null);

  // Capture the element that was focused when the dialog opened,
  // for directional animation origin and focus restoration on close.
  const triggerElementRef = useRef(null);

  // Derive dismissal behavior from purpose
  const allowEscape = purpose !== 'required';
  const allowBackdropClick = purpose === 'info';

  // Handle open/close state — skip for inline rendering
  useEffect(() => {
    if (isInline) {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (isOpen) {
      // Capture the currently focused element as the trigger — used for
      // directional animation origin and focus restoration on close.
      triggerElementRef.current = document.activeElement;

      // Set directional CSS custom properties before opening
      const trigger = triggerElementRef.current;
      if (trigger && trigger !== document.body) {
        const dir = getDialogDirection(trigger);
        dialog.style.setProperty('--dialog-dir-x', `${dir.x}px`);
        dialog.style.setProperty('--dialog-dir-y', `${dir.y}px`);
      } else {
        dialog.style.setProperty('--dialog-dir-x', '0px');
        dialog.style.setProperty('--dialog-dir-y', '16px');
      }
      if (!dialog.open) {
        dialog.showModal();
        // React's autoFocus calls .focus() during commit, before showModal()
        // makes the dialog visible, so the focus silently fails.
        // Focus the first element with data-autofocus inside the dialog.
        const autofocusTarget = dialog.querySelector('[data-autofocus]');
        if (autofocusTarget) {
          autofocusTarget.focus();
        }
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
      // Return focus to the element that opened the dialog
      triggerElementRef.current?.focus();
      triggerElementRef.current = null;
    }
  }, [isOpen, isInline]);

  // Lock body scroll when dialog is open (iOS Safari workaround)
  // Skip for inline rendering — no modal overlay to compensate for.
  useScrollLock(isOpen && !isInline);

  // Handle Escape key — skip for inline rendering
  useEffect(() => {
    if (isInline) {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog || !isOpen) {
      return;
    }
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        // Ignore IME composition-cancel, and defer to any popover/menu layered
        // on top of this dialog so a single Escape closes only the top-most
        // layer (the popover's own focus trap handles it and stops propagation).
        if (isImeKeyEvent(event) || hasActiveFocusTrapEscape()) {
          return;
        }
        event.preventDefault();
        if (allowEscape) {
          onOpenChange(false);
        }
      }
    };
    dialog.addEventListener('keydown', handleKeyDown);
    return () => dialog.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInline, allowEscape, onOpenChange]);

  // Handle backdrop click — when the user clicks the ::backdrop pseudo-element,
  // the event target is the <dialog> element itself; clicks on child content
  // always target the child. This avoids false positives from native browser
  // popups (date pickers, selects, color pickers) that render outside the
  // dialog's bounding rect.
  const handleClick = event => {
    if (event.target === event.currentTarget && allowBackdropClick) {
      onOpenChange(false);
    }
  };

  // Handle native cancel event (browser Escape handling)
  const handleCancel = event => {
    event.preventDefault();
    // Defer to a popover/menu layered on top of this dialog; it will dismiss
    // itself on the same Escape press.
    if (hasActiveFocusTrapEscape()) {
      return;
    }
    if (allowEscape) {
      onOpenChange(false);
    }
  };

  // Shared inner content wrapper
  const innerContent = /*#__PURE__*/_jsx("div", {
    ...stylex.props(styles.inner, ...container(useThemeDefault ? {
      useThemeDefault: 'dialog',
      maxHeight: isFullscreen ? undefined : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
    } : {
      paddingInnerX: paddingToken,
      paddingInnerY: paddingToken,
      paddingOuterX: paddingToken,
      paddingOuterY: paddingToken,
      maxHeight: isFullscreen ? undefined : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
    }), !useThemeDefault && effectivePadding !== 4 && paddingStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingInlineVarStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingBlockStartVarStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingBlockEndVarStyles[effectivePadding]),
    children: /*#__PURE__*/_jsx(DialogContext, {
      value: dialogContextValue,
      children: children
    })
  });

  // Filter out native open to prevent InvalidStateError when accidentally passed
  const hasPosition = position != null && !isFullscreen;
  const {
    open: _open,
    ...safeProps
  } = props;

  // --- Inline rendering path (for documentation previews) ---
  if (isInline) {
    if (!isOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      ...safeProps,
      ...mergeProps(themeProps('dialog', {
        variant
      }), stylex.props(styles.inlineWrapper, !isFullscreen && dynamicStyles.sizing(width, maxHeight), isFullscreen && styles.fullscreen, xstyle), className, style),
      children: innerContent
    });
  }

  // --- Standard modal rendering path ---

  return /*#__PURE__*/_jsx("dialog", {
    ref: mergeRefs(ref, dialogRef),
    ...safeProps,
    ...mergeProps(themeProps('dialog', {
      variant
    }), stylex.props(styles.dialog, isOpen && styles.open, styles.backdrop, !isFullscreen && dynamicStyles.sizing(width, maxHeight), hasPosition && dynamicStyles.position(position?.top, position?.right, position?.bottom, position?.left), isFullscreen && styles.fullscreen, xstyle), className, style),
    onClick: handleClick,
    onCancel: handleCancel,
    "aria-modal": "true",
    ...(purpose === 'required' ? {
      role: 'alertdialog'
    } : undefined),
    children: innerContent
  });
}
Dialog.displayName = 'Dialog';